import {
  API_ROUTES,
  PEOPLE_TEAM_ROUTES,
  INTERNAL_TEAM_ROUTES,
  SHARED_ROUTES,
} from "definitions/constants/routeConstants";
import { User } from "features/User/models";
import { generatePath as routerGeneratePath } from "react-router";
import { NewHireJourney } from "models/v2/newHireJourney";
import { truncateEnd } from "helpers/string";
import { createQueryString } from "helpers/queryString";

export function generatePath({
  path,
  params = undefined,
  query = undefined,
}: {
  path: string;
  params?: Record<string, string | undefined>;
  query?: string;
}) {
  return routerGeneratePath(query?.length ? `${path}?${query.replace(/^[?]./, "")}` : path, params);
}

export function removeLastPath(pathname: string) {
  return pathname.split("/").slice(0, -1).join("/");
}

export function removeUrlScheme(url: string) {
  return url.replace(/(^\w+:|^)\/\//, "").replace(/^www\./, "");
}

export function truncateUrl(url: string, maxLength: number = 32, ending = "...") {
  return truncateEnd(removeUrlScheme(url), maxLength, ending);
}

export function userPathById(userId: string | undefined) {
  return generatePath({ path: SHARED_ROUTES.showUser, params: { id: userId || "" } });
}

export function userPath(user: User) {
  return userPathById(user?.id);
}

export function newHireJourneysPath() {
  return generatePath({ path: INTERNAL_TEAM_ROUTES.newHireJourneys.index });
}

export function newHireJourneyPathById(newHireJourneyId: string | undefined, tabName?: string) {
  let path = INTERNAL_TEAM_ROUTES.newHireJourneys.show;
  switch (tabName) {
    case "tasks":
      path = INTERNAL_TEAM_ROUTES.newHireJourneys.tabs.tasks.base;
      break;
    case "communications":
      path = INTERNAL_TEAM_ROUTES.newHireJourneys.tabs.communications.base;
      break;
    case "meetings":
      path = INTERNAL_TEAM_ROUTES.newHireJourneys.tabs.meetings.base;
      break;
    case "fields":
      path = INTERNAL_TEAM_ROUTES.newHireJourneys.tabs.fields.base;
      break;
    case "paths":
      path = INTERNAL_TEAM_ROUTES.newHireJourneys.tabs.paths.base;
      break;
    case "activityFeed":
      path = INTERNAL_TEAM_ROUTES.newHireJourneys.tabs.activityFeed.base;
      break;
    case undefined:
      break;
    default:
      throw new Error(`Undefined tabName ${tabName}`);
      break;
  }
  return generatePath({ path, params: { id: newHireJourneyId || "" } });
}

export function newHireJourneyPath(newHireJourney: NewHireJourney) {
  return newHireJourneyPathById(newHireJourney?.id);
}

export function pendingNewHirePathById(newHireId: string) {
  return generatePath({
    path: INTERNAL_TEAM_ROUTES.newHireJourneys.pending.show,
    params: { id: newHireId || "", step: "info" },
  });
}

export function pendingNewHirePath(newHire: NewHireJourney) {
  return pendingNewHirePathById(newHire?.id);
}

export function issuesPath(newHireId?: string) {
  const query = createQueryString({ additionalQueries: { newHireId } });
  return generatePath({ path: PEOPLE_TEAM_ROUTES.issues.base, query });
}

export function issuePathById(issueId: string) {
  return generatePath({ path: PEOPLE_TEAM_ROUTES.issues.show, params: { id: issueId || "" } });
}

export function apiUserPathById(userId: string) {
  return generatePath({ path: API_ROUTES.users.show, params: { id: userId || "" } });
}

export function apiDeactivateUserPathById(userId: string) {
  return generatePath({ path: API_ROUTES.users.deactivate, params: { id: userId || "" } });
}

export function apiUpdateUserRolesPathById(userId: string) {
  return generatePath({ path: API_ROUTES.users.updateRoles, params: { id: userId || "" } });
}

export function apiPendingNewHireJourneysPath() {
  return API_ROUTES.newHireJourneys.pending.index;
}

export function apiTimezoneByPlaceId(googlePlaceId: string) {
  return generatePath({ path: API_ROUTES.autocomplete.timezones.show, params: { id: googlePlaceId || "" } });
}

export function apiCurrentCompanyPath() {
  return generatePath({ path: API_ROUTES.companies.show, params: { id: "current" } });
}

export function apiCurrentCompanyAdminPath() {
  return API_ROUTES.companies.currentAdmin;
}
